import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import { SubSectionContent } from '@shared/components/core/layouts/CommonLayoutStyles';
import SplashLoader from '@shared/components/loading/SplashLoader';
import SdkOptionsGrid from '@shared/components/support/SdkOptionsRow';
import { getDefaultClient, getDefaultDeviceType } from '@shared/hooks/helpers/projectUtils';
import AlertsContainer from '@shared/hooks/state/AlertsContainer';
import EditDeviceTypeContainer from '@shared/hooks/state/EditDeviceTypeContainer';
import { useCurrentOrgAuthClientEditor } from '@shared/hooks/useCurrentOrgAuthClientEditor';
import { useCurrentOrgDeviceTypeEditor } from '@shared/hooks/useCurrentOrgDeviceTypeEditor';
import useCurrentOrgProjectsFetching from '@shared/hooks/useCurrentOrgProjectsFetching';
import useStateRef from '@shared/hooks/useStateRef';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import ClientSecretDialog from './client/ClientSecretDialog';
import ClientAuthRedirectEditor from './client/editors/ClientAuthRedirectEditor';
import ClientDeviceTypeEditor from './client/editors/ClientDeviceTypeEditor';
import ClientNameEditor from './client/editors/ClientNameEditor';
import ClientOriginHostEditor from './client/editors/ClientOriginHostEditor';
import ClientScopeEditor from './client/editors/ClientScopeEditor';
import ClientTokenRedirectEditor from './client/editors/ClientTokenRedirectEditor';
import ProjectsCreateButtonRow from './ProjectsCreateButtonRow';

export default function ProjectsCreatePageContent() {
    const { t } = useTranslation();
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const { deviceTypes } = useCurrentOrgProjectsFetching();
    const initialClientModel = useMemo(() => getDefaultClient(organizationId || ''), [organizationId]);
    const initialDeviceModel = useMemo(() => getDefaultDeviceType(organizationId || ''), [organizationId]);
    const { showError, showSuccess } = AlertsContainer.useContainer();
    const [showSecret, setShowSecret] = useState<boolean>(false);
    const {
        editClient,
        handleInputChange,
        handleSave: handleClientSave,
    } = useCurrentOrgAuthClientEditor(initialClientModel);
    const { handleSave: handleDeviceSave } = useCurrentOrgDeviceTypeEditor(initialDeviceModel);
    const { editDeviceType, handleInputChange: handleDeviceChange = handleInputChange } =
        EditDeviceTypeContainer.useContainer();
    const [activeSection, setActiveSection, activeSectionRef] = useStateRef(1);

    const updateActiveSection = (section: number) => {
        if (section < 1 || section > 5) return;
        setActiveSection(section);
    };

    const save = async () => {
        try {
            const clientResponse = await handleClientSave();
            const deviceResponse = await handleDeviceSave();
            if (clientResponse?.resultType == 'Ok' && deviceResponse?.resultType == 'Ok') {
                setShowSecret(true);
                showSuccess(t('common:organization.projects.create.success'));
            } else {
                showError(t('common:saveError'));
            }
        } catch (error) {
            showError(t('common:saveError'));
        }
    };

    const handleCloseSecret = () => {
        setShowSecret(false);
        navigate(-1);
    };

    if (!organizationId || !editClient || !editDeviceType) return <SplashLoader />;
    return (
        <>
            <SubSectionRow
                complete={activeSection > 1}
                description={t('common:organization.projects.create.details.description')}
                number={1}
                title={t('common:organization.projects.create.details.title')}
                variant={activeSection === 1 ? 'default' : 'small'}
            />
            {activeSection === 1 && (
                <SubSectionContent>
                    <ClientNameEditor value={editClient.name} onUpdate={handleInputChange} />
                    <ProjectsCreateButtonRow active={activeSection} handleSectionChange={updateActiveSection} />
                </SubSectionContent>
            )}
            <SubSectionRow
                complete={activeSection > 2}
                description={t('common:organization.projects.sdk.description')}
                number={2}
                title={t('common:organization.projects.sdk.title')}
                variant={activeSection === 2 ? 'default' : 'small'}
            />
            {activeSection === 2 && (
                <SubSectionContent>
                    <SdkOptionsGrid />
                    <ProjectsCreateButtonRow active={activeSection} handleSectionChange={updateActiveSection} />
                </SubSectionContent>
            )}
            <SubSectionRow
                complete={activeSection > 3}
                description={t('common:organization.projects.clients.description')}
                number={3}
                title={t('common:organization.projects.clients.configure')}
                variant={activeSection === 3 ? 'default' : 'small'}
            />
            {activeSection === 3 && (
                <SubSectionContent>
                    <ClientOriginHostEditor value={editClient.originHost} onUpdate={handleInputChange} />
                    <ClientTokenRedirectEditor
                        tokenAdditionalRedirectUris={editClient.tokenAdditionalRedirectUris}
                        tokenRedirectUrlHost={editClient.tokenRedirectUrlHost}
                        onUpdate={(additionalField, additionalValue, hostField, hostValue) => {
                            handleInputChange(additionalField, additionalValue);
                            handleInputChange(hostField, hostValue);
                        }}
                    />
                    <ClientAuthRedirectEditor
                        additionalRedirectUris={editClient.additionalRedirectUris}
                        authCodeRedirectUrlHost={editClient.authCodeRedirectUrlHost}
                        onUpdate={(additionalField, additionalValue, hostField, hostValue) => {
                            handleInputChange(additionalField, additionalValue);
                            handleInputChange(hostField, hostValue);
                        }}
                    />

                    <ProjectsCreateButtonRow active={activeSection} handleSectionChange={updateActiveSection} />
                </SubSectionContent>
            )}
            <SubSectionRow
                complete={activeSection > 4}
                description={t('common:organization.projects.scopes.description')}
                number={4}
                title={t('common:organization.projects.scopes.configure')}
                variant={activeSection === 4 ? 'default' : 'small'}
            />
            {activeSection === 4 && (
                <SubSectionContent>
                    <ClientScopeEditor
                        value={editClient.authClientScopes}
                        onUpdate={(field, value) => handleInputChange(field, value)}
                    />
                    <ProjectsCreateButtonRow active={activeSection} handleSectionChange={updateActiveSection} />
                </SubSectionContent>
            )}
            <SubSectionRow
                complete={activeSection > 5}
                description={t('common:organization.projects.deviceScopes.description')}
                number={5}
                title={t('common:organization.projects.deviceScopes.configure')}
                variant={activeSection === 5 ? 'default' : 'small'}
            />
            {activeSection === 5 && (
                <SubSectionContent>
                    <ClientDeviceTypeEditor
                        value={editClient.authClientDeviceTypes}
                        onUpdate={handleInputChange}
                        deviceOptions={deviceTypes}
                    />
                    <ProjectsCreateButtonRow
                        active={activeSection}
                        handleSectionChange={updateActiveSection}
                        handleSave={save}
                    />
                </SubSectionContent>
            )}
            <ClientSecretDialog secret={editClient.secret} open={showSecret} onClose={handleCloseSecret} />
        </>
    );
}
